/**
 * The links that open the Trioty driver app on its invite screen (driver
 * backend B1, plan D-3). WhatsApp does not make a `trioty-driver://` link
 * tappable, so the link a restaurant shares is an https page on this app
 * (`DriverInviteOpenPage`), and that page hands over to the app:
 *
 * - Android: an `intent://` URL naming the package, the same shape the seller
 *   app's impersonation hand-off uses for `trioty-seller`. When the app is not
 *   installed, Chrome opens `S.browser_fallback_url` instead of an unlisted
 *   Play page: this same invite page with `?noapp=1`, which explains how to
 *   get the app (`driverInviteFallbackUrl`);
 * - iOS (and anything else): the custom scheme.
 *
 * The app's route `(auth)/invite` reads `token` (`apps/driver/app/(auth)/invite.tsx`).
 */
export const DRIVER_APP_SCHEME = "trioty-driver";
export const DRIVER_APP_ANDROID_PACKAGE = "com.trioty.driver";

/** The query flag the fallback adds: the app did not open on this phone. */
export const DRIVER_INVITE_NO_APP_PARAM = "noapp";

/**
 * The https page Chrome falls back to when the app is missing: the invite
 * page itself (origin + path, any query dropped) with `?noapp=1`. Null when
 * the page's own URL is not http(s) (Chrome ignores any other fallback).
 */
export function driverInviteFallbackUrl(pageUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(pageUrl);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  return `${url.origin}${url.pathname}?${DRIVER_INVITE_NO_APP_PARAM}=1`;
}

export function driverAppInviteLinks(
  token: string,
  fallbackUrl: string | null = null,
): {
  android: string;
  ios: string;
} {
  const query = `token=${encodeURIComponent(token)}`;
  const fallback = fallbackUrl
    ? `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`
    : "";
  return {
    android: `intent://invite?${query}#Intent;scheme=${DRIVER_APP_SCHEME};package=${DRIVER_APP_ANDROID_PACKAGE};${fallback}end`,
    ios: `${DRIVER_APP_SCHEME}://invite?${query}`,
  };
}

/** Which of the two links this device should be offered. */
export function driverAppInviteLinkFor(
  token: string,
  userAgent: string,
  fallbackUrl: string | null = null,
): string {
  const links = driverAppInviteLinks(token, fallbackUrl);
  return /android/i.test(userAgent) ? links.android : links.ios;
}
